// ============================================================================
// Office-hours week helpers
// ----------------------------------------------------------------------------
// Property `office_hours` often lists only open days (Mon–Fri). Hours UIs
// still need to show Saturday / Sunday (and any other missing weekday) as
// Closed so visitors can see the full week.
// ============================================================================

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const CLOSED_SENTINELS = new Set(['', 'false', 'null', 'undefined', '0', 'closed']);

export function hasTimeValue(val: string | null | undefined): boolean {
  if (val == null) return false;
  return !CLOSED_SENTINELS.has(val.trim().toLowerCase());
}

export function normalizeWeekday(day: string | null | undefined): WeekdayKey | null {
  if (!day) return null;
  const key = day.trim().toLowerCase().slice(0, 3);
  return (WEEKDAY_KEYS as readonly string[]).includes(key) ? (key as WeekdayKey) : null;
}

export function isClosedDay(entry: OfficeHourEntry | null | undefined): boolean {
  if (!entry) return true;
  return !hasTimeValue(entry.open_time) || !hasTimeValue(entry.close_time);
}

/** Same open/close (or both closed) so consecutive days can collapse into one row. */
export function hoursSignature(entry: OfficeHourEntry): string {
  return isClosedDay(entry) ? 'closed' : `${entry.open_time ?? ''}|${entry.close_time ?? ''}`;
}

/**
 * Returns Mon–Sun entries. Days missing from the collection are filled as
 * Closed. Unrecognized day labels (e.g. "Holiday") stay after the week.
 * Empty input stays empty so the hours placeholder still renders.
 */
export function ensureWeekWithClosedDays(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  if (!hours.length) return [];

  const byDay = new Map<WeekdayKey, OfficeHourEntry>();
  const extras: OfficeHourEntry[] = [];

  for (const entry of hours) {
    const key = normalizeWeekday(entry.day);
    if (key) {
      byDay.set(key, entry);
    } else if (entry.day?.trim()) {
      extras.push(entry);
    }
  }

  const week: OfficeHourEntry[] = WEEKDAY_KEYS.map((key) => {
    const existing = byDay.get(key);
    if (existing) {
      return { ...existing, day: WEEKDAY_LABELS[key] };
    }
    return { day: WEEKDAY_LABELS[key], open_time: null, close_time: null };
  });

  return extras.length ? [...week, ...extras] : week;
}
